/**
 * @file DealActivitiesTab.tsx
 * @description Вкладка «События» карточки сделки (phase09; phase19.31 — SC61).
 *
 * Сверху — кнопки всех видов «+ Заметка / + Задача / + Звонок / + Встреча /
 * + Письмо / + SMS» (без «⋯» — замечание владельца 2026-10-02): кнопка
 * открывает форму окном (`ActivityComposerDialog`), форма не
 * занимает экран. Ниже — список событий сделки; его устройство не
 * перестраивается (заменит общая лента phase19.18), поправлено только:
 * заметки, письма и SMS не зачёркиваются и «Выполнено» у них нет —
 * выполнить можно только задачу, звонок, встречу; шрифт не мельче 12px.
 */

import { CheckCircle2, Loader2, Plus } from "lucide-react";
import { useEffect, useState } from "react";

import { ActivityComposerDialog } from "./deal-card/ActivityComposerDialog";
import { ACTIVITY_KIND_META, COMPOSER_ACTIVITY_KINDS } from "./deal-card/activity-kinds";
import { Alert } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { EditorialCard } from "../ui/EditorialCard";
import { useNotify } from "../ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import {
  ACTIVITY_KIND_LABEL,
  completeActivity,
  listActivities,
  type ActivityDto,
  type ActivityKind,
  type DealDto,
} from "../../lib/api-crm";
import { formatShopDateTime } from "../../lib/shop-time";
import { cn } from "@/lib/utils";

interface Props {
  deal: DealDto;
  onChanged?: () => void;
}

export function DealActivitiesTab({ deal, onChanged }: Props) {
  const notify = useNotify();
  const [items, setItems] = useState<ActivityDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [composing, setComposing] = useState<ActivityKind | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await listActivities({ dealId: deal.id, limit: 100 });
      setItems(res.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить события");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id]);

  async function onComplete(activity: ActivityDto) {
    try {
      await completeActivity(activity.id);
      notify.success("Отмечено выполненным");
      void refresh();
      onChanged?.();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось отметить выполненным");
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Все виды — кнопками на виду. Телефон: сетка в две колонки (на 360
          шесть кнопок в ряд не влезают, а обрезать подпись нельзя); с 640 —
          ряд с переносом. */}
      <div role="group" aria-label="Добавить событие" className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
        {COMPOSER_ACTIVITY_KINDS.map((kind) => (
          <Button key={kind} variant="outline" className="justify-start sm:justify-center" onClick={() => setComposing(kind)}>
            <Plus className="size-4" aria-hidden />
            {ACTIVITY_KIND_META[kind].label}
          </Button>
        ))}
      </div>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="size-6 animate-spin text-on-surface-variant" />
        </div>
      ) : items.length === 0 ? (
        <EditorialCard>
          <EditorialCard.Body>
            <div className="text-center py-8 text-on-surface-variant">Событий пока нет.</div>
          </EditorialCard.Body>
        </EditorialCard>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((a) => (
            <ActivityItem key={a.id} activity={a} onComplete={() => onComplete(a)} />
          ))}
        </ul>
      )}

      {composing ? (
        <ActivityComposerDialog
          kind={composing}
          target={{ customerId: deal.customerId, dealId: deal.id }}
          onClose={() => setComposing(null)}
          onSaved={() => {
            setComposing(null);
            void refresh();
            onChanged?.();
          }}
        />
      ) : null}
    </div>
  );
}

function ActivityItem({ activity, onComplete }: { activity: ActivityDto; onComplete: () => void }) {
  const completable = ACTIVITY_KIND_META[activity.kind].completable;
  // Выполненной считаем только то, что можно выполнить: заметку не зачёркиваем.
  const isDone = completable && !!activity.completedAt;
  return (
    <EditorialCard>
      <EditorialCard.Body className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline">{ACTIVITY_KIND_LABEL[activity.kind]}</Badge>
            <span className={cn("font-medium", isDone && "line-through text-on-surface-variant")}>{activity.title}</span>
            {activity.dueAt && completable && !isDone ? (
              <Badge variant="secondary">до {formatShopDateTime(activity.dueAt)}</Badge>
            ) : null}
            {isDone ? (
              <Badge variant="default" className="gap-1">
                <CheckCircle2 className="size-3" /> Выполнено
              </Badge>
            ) : null}
          </div>
          {activity.content ? (
            <p className="text-sm text-on-surface-variant mt-1 whitespace-pre-wrap">{activity.content}</p>
          ) : null}
          <p className="type-caption text-on-surface-variant mt-2">
            {activity.createdByName ?? "—"} · {formatShopDateTime(activity.createdAt)}
          </p>
        </div>
        {completable && !isDone ? (
          <Button size="sm" variant="outline" onClick={onComplete}>
            <CheckCircle2 className="size-4 mr-1" />
            Выполнено
          </Button>
        ) : null}
      </EditorialCard.Body>
    </EditorialCard>
  );
}
